import type { FC, ReactNode } from 'react'

import { DiviMonogram, DiviWordmark } from '@/components/generals/divi-logo'
import { BadgeWordmark, NeonWordmark } from '@/components/landing/logo-artwork'
import { SectionHeading } from '@/components/landing/section-heading'
import { VariationCard } from '@/components/landing/variation-card'

const ICON_SIZES = [
    { label: '56px', className: 'size-14' },
    { label: '36px', className: 'size-9' },
    { label: '22px', className: 'size-[22px]' }
]

export const VariationsSection: FC = (): ReactNode => {
    return (
        <section id='variations' aria-labelledby='variations-title' className='py-24 sm:py-32'>
            <div className='mx-auto max-w-6xl px-6'>
                <SectionHeading id='variations-title' eyebrow="Variations" title="Four ways to wear the mark">
                    Same letterforms, same forward lean, same red. Every version is a clean vector, so it stays sharp from a tiny browser tab icon to a billboard.
                </SectionHeading>

                <div className='mt-14 grid gap-6 md:grid-cols-2'>
                    <VariationCard
                        index='01'
                        tag="Gradient"
                        name="Primary wordmark"
                        description="The signature lockup. An ember to rose gradient on deep ink, for headers, title slides, merch and anywhere the brand takes the spotlight."
                        fileName='divi-primary-wordmark'
                        pngWidth={2400}
                        stageClassName='bg-brand-ink'
                    >
                        <div aria-hidden='true' className='absolute -right-20 -top-24 -z-10 size-80 rounded-full bg-[radial-gradient(closest-side,rgba(46,116,214,0.38),transparent)]' />
                        <div aria-hidden='true' className='grain-texture absolute inset-0 -z-10 opacity-25 mix-blend-overlay' />

                        <DiviWordmark
                            data-logo-export
                            label="Divi primary wordmark"
                            className='h-auto w-[78%] max-w-[22rem] drop-shadow-[0_16px_40px_rgba(255,35,69,0.25)]'
                        />
                    </VariationCard>

                    <VariationCard
                        index='02'
                        tag="Icon"
                        name="Monogram"
                        description="The shield on its own, a V that doubles as a D. Built for app icons, avatars and favicons, and still readable at 16 pixels."
                        fileName='divi-monogram'
                        pngWidth={1024}
                        stageClassName='bg-brand-night'
                    >
                        <div aria-hidden='true' className='absolute inset-0 -z-10 bg-[radial-gradient(rgba(255,255,255,0.07)_1px,transparent_1px)] [background-size:18px_18px]' />

                        <div className='flex items-center gap-6 sm:gap-10 lg:gap-12'>
                            <DiviMonogram
                                data-logo-export
                                label="Divi monogram app icon"
                                className='size-28 drop-shadow-[0_18px_40px_rgba(255,35,69,0.22)] sm:size-36 lg:size-40'
                            />

                            <ul aria-label="The monogram at small sizes" className='flex flex-col items-start gap-3 sm:gap-4'>
                                {ICON_SIZES.map(size => (
                                    <li key={size.label} className='flex items-center gap-3'>
                                        <DiviMonogram aria-hidden='true' className={size.className} />
                                        <span className='font-mono text-[11px] text-muted-foreground'>
                                            {size.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </VariationCard>

                    <VariationCard
                        index='03'
                        tag="Line"
                        name="Neon outline"
                        description="A glowing line version with a hot core. Made for motion, event screens and late night energy, and at its best on pure black."
                        fileName='divi-neon-outline'
                        pngWidth={2400}
                        stageClassName='bg-black'
                    >
                        <div aria-hidden='true' className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(255,35,69,0.16),transparent_65%)]' />

                        <div className='w-[84%] max-w-[24rem] motion-safe:animate-[neon-flicker_8s_linear_infinite]'>
                            <NeonWordmark data-logo-export className='h-auto w-full' />
                        </div>
                    </VariationCard>

                    <VariationCard
                        index='04'
                        tag="Badge"
                        name="Knockout badge"
                        description="The wordmark cut out of a leaning red plate, so whatever sits behind it shows through. Perfect for stickers, social cards and profile banners."
                        fileName='divi-knockout-badge'
                        pngWidth={2400}
                        stageClassName='bg-brand-navy'
                    >
                        <div aria-hidden='true' className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(70,110,200,0.18),transparent_60%)]' />

                        <BadgeWordmark
                            data-logo-export
                            className='h-auto w-[76%] max-w-[21rem] drop-shadow-[0_22px_40px_rgba(0,0,0,0.55)]'
                        />
                    </VariationCard>
                </div>
            </div>
        </section>
    )
}
